<app-main-layout>
  <div class="bg-gray-50 min-h-screen">
  <div class="py-6 px-4 sm:px-6 max-w-[1500px] mx-auto">

    <!-- ============ HERO ============ -->
    <div class="relative overflow-hidden rounded-3xl bg-neutral-900 text-white px-6 sm:px-8 py-7 mb-6 shadow-lg">
      <div class="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-amber-400/20 blur-3xl"></div>
      <div class="pointer-events-none absolute right-10 bottom-0 opacity-[0.07]"><mat-icon class="!text-[160px] !h-[160px] !w-[160px]">inventory_2</mat-icon></div>

      <div class="relative flex flex-col lg:flex-row lg:items-end justify-between gap-6">
        <div class="max-w-xl">
          <div class="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-amber-400">
            <mat-icon class="!text-[14px] !h-3.5 !w-3.5">inventory_2</mat-icon> Products
          </div>
          <h1 class="mt-2 text-3xl font-bold tracking-tight">Catalogue</h1>
          <p class="mt-2 text-sm text-white/60 leading-relaxed">Search the catalogue, check stock and prices, and jump into any product to edit it.</p>
        </div>
        <div class="flex flex-wrap items-center gap-2 self-start lg:self-auto">
        @if (isAdmin) {
        <button type="button" (click)="openFixPrices()"
          matTooltip="Recalculate the 'From £…' price shown on the website for every product"
          class="flex items-center gap-2 px-4 py-2.5 rounded-2xl border border-white/15 bg-white/[0.06] text-white text-sm font-semibold hover:bg-white/[0.12] transition-colors focus:outline-none focus:ring-4 focus:ring-white/20">
          <mat-icon class="!text-[18px] !h-[18px] !w-[18px] text-amber-400">price_check</mat-icon>
          Fix "From" prices
        </button>
        }
        <button type="button"
          class="group self-start lg:self-auto flex items-center gap-2.5 pl-2.5 pr-5 py-2.5 rounded-2xl bg-amber-400 text-neutral-900 font-bold text-sm shadow-lg shadow-amber-500/20 hover:bg-amber-300 hover:-translate-y-0.5 active:translate-y-0 transition-all focus:outline-none focus:ring-4 focus:ring-amber-400/40"
          (click)="onAddProduct()">
          <span class="h-8 w-8 rounded-xl bg-neutral-900 text-amber-400 flex items-center justify-center transition-transform duration-200 group-hover:rotate-90">
            <mat-icon class="!text-[20px] !h-5 !w-5">add</mat-icon>
          </span>
          New product
        </button>
        </div>
      </div>

      <div class="relative mt-7 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        @for (s of [
          { key: 'ALL', label: 'Products', value: result?.counts?.total, icon: 'apps', tone: '' },
          { key: 'ACTIVE', label: 'Active', value: result?.counts?.active, icon: 'check_circle', tone: 'text-emerald-300' },
          { key: 'LOW', label: 'Low stock', value: result?.counts?.lowStock, icon: 'trending_down', tone: 'text-orange-300' },
          { key: 'OUT', label: 'Out of stock', value: result?.counts?.outOfStock, icon: 'block', tone: 'text-red-300' },
          { key: 'OFFER', label: 'On offer', value: result?.counts?.onOffer, icon: 'sell', tone: 'text-rose-300' }
        ]; track s.key) {
        <button type="button" class="text-left rounded-2xl border px-4 py-3 transition-all"
          [ngClass]="view === s.key ? 'bg-amber-400/15 border-amber-400/50' : 'bg-white/[0.06] border-white/10 hover:bg-white/[0.1]'" (click)="setView($any(s.key))">
          <div class="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide" [ngClass]="s.tone || 'text-white/50'"><mat-icon class="!text-[14px] !h-3.5 !w-3.5">{{ s.icon }}</mat-icon>{{ s.label }}</div>
          <div class="mt-1 text-2xl font-bold tabular-nums">{{ s.value === undefined || s.value === null ? '–' : (s.value | number) }}</div>
        </button>
        }
      </div>
    </div>

    <!-- ============ TOOLBAR ============ -->
    <div class="sticky top-0 z-20 -mx-4 sm:mx-0 px-4 sm:px-0 py-3 mb-4 bg-gray-50/90 backdrop-blur">
      <div class="flex flex-col lg:flex-row lg:items-center gap-3">
        <div class="relative flex-1">
          <mat-icon class="absolute left-3.5 top-1/2 -translate-y-1/2 !text-gray-400 !text-[20px] !h-5 !w-5 pointer-events-none">search</mat-icon>
          <input type="text" name="product-name"
            class="w-full pl-11 pr-10 py-2.5 rounded-xl border border-gray-200 bg-white shadow-sm focus:border-amber-400 focus:ring-4 focus:ring-amber-100 outline-none transition-all text-sm"
            placeholder="Search by name, SKU or category…"
            [ngModel]="prodKeyword" (ngModelChange)="onKeywordChange($event)" (keyup.enter)="onSearch()" />
          @if (prodKeyword) {
          <button class="absolute right-2.5 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 flex items-center justify-center" (click)="clearSearch()" aria-label="Clear search">
            <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">close</mat-icon>
          </button>
          }
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <div class="flex flex-wrap bg-white border border-gray-200 rounded-xl p-1 gap-1 shadow-sm">
            @for (v of views; track v.key) {
            <button class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all"
              [ngClass]="view === v.key ? 'bg-neutral-900 text-white shadow-sm' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'" (click)="setView(v.key)">
              {{ v.label }}
              @if (v.count !== undefined) { <span class="tabular-nums" [ngClass]="view === v.key ? 'text-amber-400' : 'text-gray-400'">{{ v.count | number }}</span> }
            </button>
            }
          </div>
          <button class="flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 bg-white shadow-sm text-xs font-semibold text-gray-700 hover:border-gray-300" [matMenuTriggerFor]="sortMenu">
            <mat-icon class="!text-[16px] !h-4 !w-4 !text-gray-400">sort</mat-icon>{{ sortLabel }}<mat-icon class="!text-[16px] !h-4 !w-4 !text-gray-400">expand_more</mat-icon>
          </button>
          <mat-menu #sortMenu="matMenu">
            @for (o of sortOptions; track o.key) {
            <button mat-menu-item (click)="setSort(o.key)">
              <span class="flex items-center gap-2" [class.font-semibold]="sort === o.key">
                <mat-icon class="!text-[18px] !h-[18px] !w-[18px] !mr-0" [class.invisible]="sort !== o.key">check</mat-icon>{{ o.label }}
              </span>
            </button>
            }
          </mat-menu>
          <button class="h-9 w-9 rounded-xl border border-gray-200 bg-white shadow-sm text-gray-400 hover:text-gray-800 flex items-center justify-center" (click)="loadProducts()" matTooltip="Refresh" aria-label="Refresh">
            <mat-icon class="!text-[18px] !h-[18px] !w-[18px]" [class.animate-spin]="loading">refresh</mat-icon>
          </button>
        </div>
      </div>
      <div class="mt-2 flex items-center justify-between text-xs text-gray-400 px-1">
        <span>{{ loading ? 'Loading…' : rangeLabel }}</span>
        @if (hasFilters) {
          <button class="flex items-center gap-1 font-semibold text-amber-700 hover:underline" (click)="clearFilters()"><mat-icon class="!text-[14px] !h-3.5 !w-3.5">close</mat-icon>Clear filters</button>
        }
      </div>
    </div>

    <!-- ============ LIST ============ -->
    @if (loading && !products.length) {
      <div class="flex flex-col gap-2.5">
        @for (i of [1, 2, 3, 4, 5, 6]; track i) {
        <div class="flex items-center gap-4 rounded-2xl bg-white border border-gray-100 p-4 animate-pulse">
          <div class="h-16 w-16 rounded-xl bg-gray-100"></div>
          <div class="flex-1"><div class="h-4 w-1/3 rounded bg-gray-100"></div><div class="mt-2 h-3 w-1/5 rounded bg-gray-100"></div><div class="mt-2 h-3 w-1/4 rounded bg-gray-100"></div></div>
          <div class="hidden md:block h-5 w-20 rounded bg-gray-100"></div>
          <div class="hidden md:block h-2 w-32 rounded-full bg-gray-100"></div>
        </div>
        }
      </div>
    } @else if (loadError) {
      <div class="rounded-3xl border-2 border-dashed border-red-200 bg-white py-16 text-center">
        <span class="mx-auto h-14 w-14 rounded-2xl bg-red-50 text-red-500 flex items-center justify-center"><mat-icon class="!text-[28px] !h-7 !w-7">cloud_off</mat-icon></span>
        <p class="mt-3 text-sm font-semibold text-gray-700">Could not load products</p>
        <button class="mt-3 text-xs font-semibold text-amber-700 hover:underline" (click)="loadProducts()">Try again</button>
      </div>
    } @else if (!products.length) {
      <div class="rounded-3xl border-2 border-dashed border-gray-200 bg-white py-16 text-center">
        <span class="mx-auto h-14 w-14 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center"><mat-icon class="!text-[28px] !h-7 !w-7">{{ hasFilters ? 'search_off' : 'inventory_2' }}</mat-icon></span>
        <p class="mt-3 text-sm font-semibold text-gray-700">{{ hasFilters ? 'No products match those filters' : 'No products yet' }}</p>
        @if (hasFilters) { <button class="mt-3 text-xs font-semibold text-amber-700 hover:underline" (click)="clearFilters()">Clear search and filters</button> }
      </div>
    } @else {
      <!-- Column headings -->
      <div class="hidden lg:grid grid-cols-[minmax(0,1fr)_150px_200px_110px_100px_150px] gap-4 px-5 pb-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
        <span>Product</span><span>Price</span><span>Stock</span><span>Options</span><span>Status</span><span class="text-right">Actions</span>
      </div>

      <div class="flex flex-col gap-2.5 transition-opacity" [class.opacity-50]="loading">
        @for (p of products; track p.productId) {
        <div class="group grid grid-cols-[minmax(0,1fr)_auto] lg:grid-cols-[minmax(0,1fr)_150px_200px_110px_100px_150px] gap-x-4 gap-y-3 items-center rounded-2xl bg-white border border-gray-100 hover:border-gray-200 p-3 sm:p-4 lg:px-5 transition-all duration-200 hover:-translate-y-px hover:shadow-[0_8px_24px_-12px_rgba(0,0,0,0.18)]">

          <!-- Product -->
          <div class="flex items-center gap-4 min-w-0">
            <button type="button" class="relative flex-shrink-0 rounded-xl focus:outline-none focus-visible:ring-4 focus-visible:ring-amber-100" (click)="onView(p)" [attr.aria-label]="'View ' + p.productName">
              @if (p.image) {
                <img [src]="p.image" [alt]="p.productName" class="h-16 w-16 rounded-xl object-cover border border-gray-100 transition-transform duration-200 group-hover:scale-[1.03]" [class.grayscale]="!p.active" loading="lazy" />
              } @else {
                <div class="h-16 w-16 rounded-xl border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center text-gray-300"><mat-icon>image</mat-icon></div>
              }
              @if (p.isFeatured) {
                <span class="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-amber-400 text-neutral-900 flex items-center justify-center shadow ring-2 ring-white" matTooltip="Featured"><mat-icon class="!text-[12px] !h-3 !w-3">star</mat-icon></span>
              }
              @if (p.saving > 0) {
                <span class="absolute -bottom-1.5 left-1/2 -translate-x-1/2 text-[10px] font-bold px-1.5 py-px rounded-md bg-rose-500 text-white shadow ring-2 ring-white whitespace-nowrap">-{{ p.saving }}%</span>
              }
            </button>
            <div class="min-w-0">
              @if (isAdmin) {
                <a class="block font-semibold text-gray-900 leading-snug truncate hover:text-amber-700 cursor-pointer" (click)="onEditClick(p)" [title]="p.productName">{{ p.productName }}</a>
              } @else {
                <div class="font-semibold text-gray-900 leading-snug truncate" [title]="p.productName">{{ p.productName }}</div>
              }
              <div class="mt-1 flex flex-wrap items-center gap-1.5">
                @if (p.category) { <span class="text-[11px] font-medium px-2 py-0.5 rounded-md bg-gray-100 text-gray-600">{{ p.category }}</span> }
                @if (p.label) { <span class="text-[11px] font-semibold px-2 py-0.5 rounded-md bg-rose-50 text-rose-700">{{ p.label }}</span> }
              </div>
              <div class="mt-1 text-[11px] font-mono text-gray-400 truncate" [matTooltip]="p.skus">
                {{ skuPreview(p).first }}@if (skuPreview(p).more) { <span class="text-gray-300"> +{{ skuPreview(p).more }} more</span> }
              </div>
            </div>
          </div>

          <!-- Status (top right on phones) -->
          <div class="lg:order-5">
            <span class="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg" [ngClass]="p.active ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'">
              <span class="relative flex h-2 w-2">
                @if (p.active) { <span class="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 animate-ping"></span> }
                <span class="relative inline-flex h-2 w-2 rounded-full" [ngClass]="p.active ? 'bg-emerald-500' : 'bg-gray-400'"></span>
              </span>
              {{ p.active ? 'Active' : 'Inactive' }}
            </span>
          </div>

          <!-- Price -->
          <div class="lg:order-2 whitespace-nowrap">
            <div class="text-[10px] font-semibold uppercase tracking-wide text-gray-400 lg:hidden">Price</div>
            @if (p.fromPrice) {
              <div class="font-bold text-gray-900 tabular-nums">£{{ p.fromPrice | number:'1.2-2' }}</div>
              @if (p.toPrice && p.toPrice > p.fromPrice) { <div class="text-xs text-gray-400 tabular-nums">to £{{ p.toPrice | number:'1.2-2' }}</div> }
            } @else {
              <span class="text-gray-300">—</span>
            }
          </div>

          <!-- Stock -->
          <div class="lg:order-3 min-w-0">
            <div class="text-[10px] font-semibold uppercase tracking-wide text-gray-400 lg:hidden">Stock</div>
            <div class="flex items-center gap-2">
              <span class="text-base font-bold text-gray-900 tabular-nums">{{ p.stock | number }}</span>
              <span class="text-[11px] font-semibold px-2 py-0.5 rounded-md" [ngClass]="stockState(p).cls">{{ stockState(p).label }}</span>
            </div>
            @if (p.stock > 0) {
              <div class="mt-1.5 flex h-1.5 rounded-full overflow-hidden bg-gray-100" [matTooltip]="'Markstey ' + p.mStock + ' · Kettering ' + p.kStock">
                <div class="h-full bg-amber-400" [style.width.%]="(p.mStock / Math.max(p.mStock + p.kStock, 1)) * 100"></div>
                <div class="h-full bg-sky-400" [style.width.%]="(p.kStock / Math.max(p.mStock + p.kStock, 1)) * 100"></div>
              </div>
            }
            <div class="mt-1 flex items-center gap-3 text-[11px] text-gray-400">
              <span class="flex items-center gap-1"><span class="h-1.5 w-1.5 rounded-full bg-amber-400"></span>Markstey {{ p.mStock | number }}</span>
              <span class="flex items-center gap-1"><span class="h-1.5 w-1.5 rounded-full bg-sky-400"></span>Kettering {{ p.kStock | number }}</span>
            </div>
          </div>

          <!-- Options -->
          <div class="lg:order-4">
            <div class="text-[10px] font-semibold uppercase tracking-wide text-gray-400 lg:hidden">Options</div>
            <span class="inline-flex items-center gap-1 text-xs font-semibold text-gray-700"><mat-icon class="!text-[15px] !h-[15px] !w-[15px] text-gray-400">layers</mat-icon>{{ p.variants }} option{{ p.variants === 1 ? '' : 's' }}</span>
            @if (p.rating) {
              <div class="mt-1 flex items-center gap-1 text-xs text-amber-700 font-semibold"><mat-icon class="!text-[14px] !h-[14px] !w-[14px] !text-amber-500">star</mat-icon>{{ p.rating | number:'1.1-1' }}</div>
            }
          </div>

          <!-- Actions -->
          <div class="lg:order-6 col-span-2 lg:col-span-1 flex items-center justify-end">
            <div class="flex items-center gap-0.5 rounded-xl bg-gray-50 border border-gray-100 p-0.5 lg:opacity-0 lg:group-hover:opacity-100 lg:group-focus-within:opacity-100 transition-opacity">
              <button class="h-8 w-8 rounded-lg text-gray-500 hover:bg-white hover:text-sky-600 hover:shadow-sm flex items-center justify-center" (click)="onView(p)" matTooltip="View details and stock" aria-label="View"><mat-icon class="!text-[18px] !h-[18px] !w-[18px]">visibility</mat-icon></button>
              @if (isAdmin) {
                <button class="h-8 w-8 rounded-lg text-gray-500 hover:bg-white hover:text-amber-700 hover:shadow-sm flex items-center justify-center" (click)="onEditClick(p)" matTooltip="Edit product" aria-label="Edit"><mat-icon class="!text-[18px] !h-[18px] !w-[18px]">edit</mat-icon></button>
                <button class="h-8 w-8 rounded-lg text-gray-500 hover:bg-white hover:text-violet-600 hover:shadow-sm flex items-center justify-center" (click)="onOpenCMS(p)" matTooltip="Edit the page content" aria-label="Edit page content"><mat-icon class="!text-[18px] !h-[18px] !w-[18px]">view_compact_alt</mat-icon></button>
              }
              <a class="h-8 w-8 rounded-lg text-gray-500 hover:bg-white hover:text-emerald-600 hover:shadow-sm flex items-center justify-center" [href]="frontEndURL + p.productSlug" target="_blank" rel="noopener" matTooltip="Open on the website" aria-label="Open on the website"><mat-icon class="!text-[18px] !h-[18px] !w-[18px]">open_in_new</mat-icon></a>
            </div>
          </div>
        </div>
        }
      </div>

      <div class="mt-4 rounded-2xl bg-white border border-gray-100 shadow-sm overflow-hidden">
        <mat-paginator
          [pageSizeOptions]="[10, 20, 50, 100]"
          showFirstLastButtons
          [length]="count"
          [pageSize]="itemsPerPage"
          [pageIndex]="pageNumber"
          (page)="updateList($event)">
        </mat-paginator>
      </div>
    }
  </div>
  </div>

  <!-- ============ FIX "FROM" PRICES ============ -->
  @if (fixOpen) {
  <div class="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-neutral-900/50 backdrop-blur-sm p-0 sm:p-4" (click)="!fixApplying && (fixOpen = false)">
    <div class="w-full sm:max-w-3xl max-h-[90vh] flex flex-col rounded-t-3xl sm:rounded-3xl bg-white shadow-2xl" (click)="$event.stopPropagation()" role="dialog" aria-modal="true" aria-label="Fix From prices">
      <div class="flex items-start gap-3 px-6 pt-6 pb-4 border-b border-gray-100">
        <span class="h-10 w-10 shrink-0 rounded-2xl bg-amber-100 text-amber-700 flex items-center justify-center"><mat-icon>price_check</mat-icon></span>
        <div class="flex-1 min-w-0">
          <h2 class="text-lg font-bold text-gray-900">Fix "From" prices</h2>
          <p class="mt-0.5 text-sm text-gray-500">The "From £…" price on the website is the lowest price a product sells for - the sale price when it's on offer. Paving shows the lowest price per m².</p>
        </div>
        <button type="button" class="h-8 w-8 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 flex items-center justify-center" (click)="fixOpen = false" [disabled]="fixApplying" aria-label="Close">
          <mat-icon class="!text-[20px] !h-5 !w-5">close</mat-icon>
        </button>
      </div>

      <div class="flex-1 overflow-y-auto px-6 py-4">
        @if (fixLoading) {
          <div class="flex items-center justify-center gap-3 py-12 text-sm text-gray-500"><mat-spinner diameter="24"></mat-spinner> Checking every product…</div>
        } @else if (fixResult) {
          @if (!fixResult.changes.length) {
            <div class="flex flex-col items-center py-10 text-center">
              <span class="h-12 w-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center"><mat-icon>check_circle</mat-icon></span>
              <p class="mt-3 font-semibold text-gray-900">All {{ fixResult.checked | number }} products are correct</p>
              <p class="text-sm text-gray-500">Nothing to fix.</p>
            </div>
          } @else {
            <p class="text-sm text-gray-600"><span class="font-bold text-gray-900">{{ fixResult.changes.length }}</span> of {{ fixResult.checked | number }} products show the wrong "From" price. Check the list, then update them all.</p>
            <div class="mt-3 rounded-2xl border border-gray-100 overflow-hidden">
              <table class="w-full text-sm">
                <thead class="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
                  <tr><th class="text-left font-semibold px-4 py-2">Product</th><th class="text-right font-semibold px-4 py-2 whitespace-nowrap">Now</th><th class="px-1"></th><th class="text-right font-semibold px-4 py-2 whitespace-nowrap">Will be</th></tr>
                </thead>
                <tbody>
                  @for (c of fixResult.changes; track c.productId) {
                  <tr class="border-t border-gray-100">
                    <td class="px-4 py-2.5">
                      <a [href]="frontEndURL + '/product/' + c.productSlug" target="_blank" rel="noopener" class="font-medium text-gray-900 hover:text-amber-700 hover:underline">{{ c.productName }}</a>
                      <div class="mt-0.5 flex gap-1.5">
                        @if (c.perM2) { <span class="rounded-full bg-sky-50 px-2 py-0.5 text-[10px] font-semibold text-sky-700">per m²</span> }
                        @if (!c.active) { <span class="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-500">inactive</span> }
                      </div>
                    </td>
                    <td class="px-4 py-2.5 text-right tabular-nums text-gray-400 line-through whitespace-nowrap">£{{ c.oldMinPrice | number: '1.2-2' }}</td>
                    <td class="px-1 text-gray-300"><mat-icon class="!text-[16px] !h-4 !w-4 align-middle">arrow_forward</mat-icon></td>
                    <td class="px-4 py-2.5 text-right tabular-nums font-bold whitespace-nowrap" [ngClass]="c.newMinPrice < c.oldMinPrice ? 'text-emerald-700' : 'text-gray-900'">£{{ c.newMinPrice | number: '1.2-2' }}</td>
                  </tr>
                  }
                </tbody>
              </table>
            </div>
            <p class="mt-3 text-xs text-gray-500">A price that looks too low or too high usually means one of that product's variants has the wrong price - open the product to check it before updating.</p>
          }
        }
      </div>

      <div class="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100">
        <button type="button" class="px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-100" (click)="fixOpen = false" [disabled]="fixApplying">{{ fixResult?.changes?.length ? 'Cancel' : 'Close' }}</button>
        @if (fixResult?.changes?.length) {
        <button type="button" (click)="applyFixPrices()" [disabled]="fixApplying"
          class="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-neutral-900 text-white text-sm font-bold hover:bg-neutral-800 disabled:opacity-60">
          @if (fixApplying) { <mat-spinner diameter="16" color="accent"></mat-spinner> }
          Update {{ fixResult!.changes.length }} product{{ fixResult!.changes.length === 1 ? '' : 's' }}
        </button>
        }
      </div>
    </div>
  </div>
  }
</app-main-layout>
